"use client";

import { motion } from "framer-motion";
import { useState } from "react";
import { useCart } from "@/lib/cart-store";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Search, ShoppingCart, Sparkles, Filter } from "lucide-react";
import { toast } from "sonner";

type StoreProduct = {
  id: string;
  name: string;
  range: string;
  desc: string;
  price: number;
  unit: string;
  image: string;
  badge?: string;
  category: "racine" | "farine" | "snack" | "preparation" | "boisson";
};

const allProducts: StoreProduct[] = [
  // PATATE OR
  { id: "patate-frais", name: "Patate Douce Fraîche", range: "PATATE OR", desc: "Racine lavée, calibre premium, peau ambrée.", price: 1200, unit: "kg", image: "/images/patate-douce.png", badge: "Best-seller", category: "racine" },
  { id: "patate-farine", name: "Farine de Patate Douce", range: "PATATE OR", desc: "Farine fine sans gluten, riche en fibres.", price: 2500, unit: "kg", image: "/images/gamme-patate.png", category: "farine" },
  { id: "patate-chips", name: "Chips de Patate Douce", range: "PATATE OR", desc: "Tranches fines, frites à l'huile de palme.", price: 1500, unit: "sachet 250g", image: "/images/chips-patate.png", badge: "Artisanal", category: "snack" },
  { id: "patate-puree", name: "Purée de Patate Douce", range: "PATATE OR", desc: "Purée prête à réchauffer, rien d'ajouté.", price: 1800, unit: "pot 500g", image: "/images/gamme-patate.png", category: "preparation" },
  { id: "patate-sirop", name: "Sirop de Patate Douce", range: "PATATE OR", desc: "Sirop naturel, alternative au sucre raffiné.", price: 3500, unit: "flacon 50cl", image: "/images/gamme-patate.png", badge: "Premium", category: "preparation" },

  // POMME DE TERRE OR
  { id: "pdt-frais", name: "Pomme de Terre Fraîche", range: "POMME DE TERRE OR", desc: "Calibre homogène, peau fine dorée.", price: 900, unit: "kg", image: "/images/pomme-de-terre.png", category: "racine" },
  { id: "pdt-farine", name: "Farine de Pomme de Terre", range: "POMME DE TERRE OR", desc: "Farine blanche, idéale pâtisserie.", price: 2800, unit: "kg", image: "/images/gamme-pdt.png", category: "farine" },
  { id: "pdt-flocons", name: "Flocons de Purée", range: "POMME DE TERRE OR", desc: "Flocons déshydratés, prêts en 3 minutes.", price: 2000, unit: "boîte 400g", image: "/images/gamme-pdt.png", category: "preparation" },
  { id: "pdt-chips", name: "Chips de Pomme de Terre", range: "POMME DE TERRE OR", desc: "Fraîchement coupées, sel de mer.", price: 1400, unit: "sachet 250g", image: "/images/chips-patate.png", category: "snack" },
  { id: "pdt-vodka", name: "Vodka de Pomme de Terre", range: "POMME DE TERRE OR", desc: "Spiritueux artisanal, triple distillation.", price: 8000, unit: "flacon 70cl", image: "/images/gamme-pdt.png", badge: "Édition limitée", category: "boisson" },

  // MANIOC OR
  { id: "manioc-frais", name: "Manioc Frais", range: "MANIOC OR", desc: "Racine récoltée du jour, chair blanche.", price: 600, unit: "kg", image: "/images/manioc.png", category: "racine" },
  { id: "manioc-farine", name: "Farine de Manioc", range: "MANIOC OR", desc: "Fermentation naturelle, séchage solaire.", price: 2500, unit: "kg", image: "/images/farine-manioc.png", badge: "Sans gluten", category: "farine" },
  { id: "manioc-attieke", name: "Attiéké", range: "MANIOC OR", desc: "Couscous de manioc fermenté, prêt à déguster.", price: 1800, unit: "kg", image: "/images/attieke.png", badge: "Recette héritée", category: "preparation" },
  { id: "manioc-gari", name: "Gari", range: "MANIOC OR", desc: "Semoule grillée, snack ou accompagnement.", price: 2000, unit: "sachet 1kg", image: "/images/gamme-manioc.png", category: "farine" },
  { id: "manioc-tapioca", name: "Tapioca", range: "MANIOC OR", desc: "Perles de manioc, desserts et potages.", price: 3200, unit: "sachet 500g", image: "/images/gamme-manioc.png", badge: "Premium", category: "preparation" },

  // IGNAME OR
  { id: "igname-frais", name: "Igname Frais", range: "IGNAME OR", desc: "Tubercule calibre royal, chair crème.", price: 1000, unit: "kg", image: "/images/igname.png", badge: "Édition noble", category: "racine" },
  { id: "igname-farine", name: "Farine d'Igname", range: "IGNAME OR", desc: "Farine fine, base du foutou moderne.", price: 3000, unit: "kg", image: "/images/gamme-igname.png", category: "farine" },
  { id: "igname-foutou", name: "Foutou Mix", range: "IGNAME OR", desc: "Mélange prêt à préparer, igname + banane.", price: 2200, unit: "sachet 1kg", image: "/images/gamme-igname.png", category: "preparation" },
  { id: "igname-chips", name: "Chips d'Igname", range: "IGNAME OR", desc: "Tranches fines, sel de Guérande.", price: 1700, unit: "sachet 250g", image: "/images/chips-patate.png", badge: "Artisanal", category: "snack" },
];

const ranges = ["Toutes", "PATATE OR", "POMME DE TERRE OR", "MANIOC OR", "IGNAME OR"];
const categories = [
  { id: "all", label: "Tous" },
  { id: "racine", label: "Racines fraîches" },
  { id: "farine", label: "Farines" },
  { id: "snack", label: "Snacks" },
  { id: "preparation", label: "Préparations" },
  { id: "boisson", label: "Boissons" },
];

function ProductCard({ p, index }: { p: StoreProduct; index: number }) {
  const { addItem, setOpen } = useCart();

  const handleAdd = () => {
    addItem({
      id: p.id,
      name: p.name,
      range: p.range,
      price: p.price,
      unit: p.unit,
      image: p.image,
    });
    toast.success(`${p.name} ajouté au panier`, {
      description: `${p.price.toLocaleString("fr-FR")} FCFA / ${p.unit}`,
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.5, delay: (index % 4) * 0.05 }}
    >
      <Card className="group relative overflow-hidden border-border hover:border-gold hover:shadow-gold-lg transition-all duration-500 rounded-2xl bg-card h-full flex flex-col">
        <div className="relative h-48 overflow-hidden">
          <img
            src={p.image}
            alt={p.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-earth-deep/70 via-transparent to-transparent" />

          {p.badge && (
            <div className="absolute top-3 left-3">
              <Badge className="bg-gradient-gold text-white shadow-gold border-0 text-[10px]">
                <Sparkles className="w-3 h-3 mr-1" />
                {p.badge}
              </Badge>
            </div>
          )}

          <div className="absolute top-3 right-3">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-cream/90 backdrop-blur text-[9px] font-bold uppercase tracking-wider text-earth-deep">
              {p.range}
            </span>
          </div>
        </div>

        <div className="p-4 flex-1 flex flex-col">
          <h3 className="font-display text-base font-bold text-foreground leading-tight">
            {p.name}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground leading-snug line-clamp-2 flex-1">
            {p.desc}
          </p>

          <div className="mt-3 flex items-center justify-between">
            <div>
              <div className="font-display text-lg font-extrabold text-gradient-gold">
                {p.price.toLocaleString("fr-FR")}
                <span className="text-xs text-muted-foreground ml-1 font-normal">FCFA</span>
              </div>
              <div className="text-[10px] text-muted-foreground">/ {p.unit}</div>
            </div>
            <Button
              size="sm"
              onClick={handleAdd}
              className="bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full h-9 w-9 p-0"
              aria-label={`Ajouter ${p.name} au panier`}
            >
              <Plus className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export function Store() {
  const [range, setRange] = useState("Toutes");
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const { setOpen, totalItems } = useCart();

  const filtered = allProducts.filter((p) => {
    if (range !== "Toutes" && p.range !== range) return false;
    if (category !== "all" && p.category !== category) return false;
    if (query && !p.name.toLowerCase().includes(query.toLowerCase()) && !p.desc.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  return (
    <section id="boutique" className="relative py-24 lg:py-32 bg-gradient-to-b from-secondary/40 to-background">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="divider-gold max-w-xs mx-auto mb-5"
          >
            <span className="text-xs uppercase tracking-[0.32em] text-gold-deep font-semibold">
              Boutique en Ligne
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight"
          >
            La <span className="text-gradient-gold">boutique</span> PATATE OR
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed"
          >
            Commandez en ligne nos quatre gammes de produits transformés. Paiement à la
            livraison, expédition nationale et internationale. Fraîcheur garantie.
          </motion.p>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-12 p-5 rounded-2xl glass-card"
        >
          {/* Search + range select */}
          <div className="grid sm:grid-cols-12 gap-3 items-center">
            <div className="sm:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher un produit..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 rounded-full bg-card border-border"
              />
            </div>
            <div className="sm:col-span-4">
              <Select value={range} onValueChange={setRange}>
                <SelectTrigger className="rounded-full bg-card border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ranges.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-3 flex justify-end">
              <Button
                onClick={() => setOpen(true)}
                className="w-full bg-gradient-gold text-white shadow-gold hover:shadow-gold-lg rounded-full relative"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Panier
                {totalItems() > 0 && (
                  <span className="ml-2 grid place-items-center min-w-5 h-5 px-1 rounded-full bg-white text-earth-deep text-[10px] font-bold">
                    {totalItems()}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Category pills */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground mr-1">
              <Filter className="w-3 h-3" />
              Catégorie :
            </span>
            {categories.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
                  category === c.id
                    ? "bg-gradient-gold text-white shadow-gold"
                    : "bg-secondary text-foreground/70 hover:bg-secondary/70"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </motion.div>

        {/* Results count */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            <span className="font-bold text-foreground">{filtered.length}</span> produit(s) trouvé(s)
          </p>
        </div>

        {/* Products grid */}
        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} p={p} index={i} />
            ))}
          </div>
        ) : (
          <div className="mt-12 text-center py-16">
            <Search className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="font-display text-lg font-bold text-foreground">Aucun produit trouvé</p>
            <p className="text-sm text-muted-foreground mt-1">Essayez d'autres critères de recherche.</p>
          </div>
        )}

        {/* Trust strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mt-14 grid sm:grid-cols-4 gap-4"
        >
          {[
            { icon: "🚚", title: "Livraison nationale", text: "Sous 24–72h" },
            { icon: "🌍", title: "Export international", text: "Nigeria · Europe · Oumma" },
            { icon: "💳", title: "Paiement à la livraison", text: "Ou mobile money" },
            { icon: "✓", title: "Qualité certifiée", text: "HACCP · Halal" },
          ].map((t, i) => (
            <div key={i} className="p-4 rounded-xl glass-card text-center">
              <div className="text-2xl">{t.icon}</div>
              <div className="mt-2 font-display text-sm font-bold text-foreground">{t.title}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{t.text}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
